import { useEffect, useState } from 'react';

/**
 * Rotating set of "case" images for the hero, each with a distinctly
 * shaped frame (varying border radius, thickness, rotation, and accent
 * color) rather than a single uniform rectangle — the brief specifically
 * asked to avoid "boring rectangle" framing here. Photos are themed
 * Unsplash stock (business/office/paperwork subjects appropriate for a
 * tax consultancy) used as placeholders until the client supplies real
 * photography.
 */
const SLIDES = [
  {
    src: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80',
    alt: 'Consultant reviewing paperwork with a client',
    caption: 'Every filing, reviewed by an expert',
    frame: 'blob' as const,
    accent: 'seal-blue' as const,
  },
  {
    src: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=800&q=80',
    alt: 'Small business owner at their shop counter',
    caption: 'Trusted by local businesses across Purulia',
    frame: 'notched' as const,
    accent: 'marigold' as const,
  },
  {
    src: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&q=80',
    alt: 'Team collaborating over documents',
    caption: 'Transparent process, no hidden surprises',
    frame: 'tilted' as const,
    accent: 'ledger-green' as const,
  },
];

const ACCENT_CLASSES = {
  'seal-blue': { border: 'border-seal-blue', dot: 'bg-seal-blue', bg: 'bg-seal-blue/10' },
  marigold: { border: 'border-marigold', dot: 'bg-marigold', bg: 'bg-marigold/15' },
  'ledger-green': { border: 'border-ledger-green', dot: 'bg-ledger-green', bg: 'bg-ledger-green/10' },
};

export function HeroCarousel() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setActive((i) => (i + 1) % SLIDES.length), 4500);
    return () => clearInterval(timer);
  }, []);

  const slide = SLIDES[active];
  const accent = ACCENT_CLASSES[slide.accent];

  return (
    // w-full here is load-bearing, not decorative: this div sits inside
    // nested flex containers with no defined width of their own, and
    // every size below it (w-full, w-[85%] on the caption, etc.) is a
    // percentage. Without an explicit width to anchor that chain, all
    // of those percentages resolve against an effectively-zero width,
    // collapsing the whole card (photo + frame + caption) to nothing —
    // which is exactly the "missing photo, sliver-width caption" bug
    // this was causing.
    <div className="relative flex w-full items-center justify-center">
      {/* Decorative floating shapes behind the frame — dots, a ring, a
          soft blob — purely for visual texture so the hero isn't just
          "photo on white background." */}
      <div className="pointer-events-none absolute -left-6 -top-6 h-16 w-16 animate-float rounded-full bg-marigold/30 blur-sm sm:-left-10 sm:-top-10 sm:h-24 sm:w-24" />
      <div className="pointer-events-none absolute -bottom-8 -right-4 h-20 w-20 animate-spin-slow rounded-full border-4 border-dashed border-ledger-green/30 sm:h-28 sm:w-28" />
      <div className="pointer-events-none absolute right-10 top-2 h-3 w-3 rounded-full bg-seal-blue/40" />
      <div className="pointer-events-none absolute bottom-4 left-1/3 h-2 w-2 rounded-full bg-marigold/50" />

      <div className="relative w-full max-w-sm">
        {SLIDES.map((s, i) => {
          const isActive = i === active;
          const frameClass =
            s.frame === 'blob'
              ? 'rounded-[3rem] rounded-tr-lg'
              : s.frame === 'notched'
                ? 'rounded-2xl [clip-path:polygon(0%_0%,88%_0%,100%_12%,100%_100%,12%_100%,0%_88%)]'
                : 'rounded-3xl';
          const rotateClass = s.frame === 'tilted' ? 'rotate-2' : s.frame === 'notched' ? '-rotate-1' : '';
          const slideAccent = ACCENT_CLASSES[s.accent];

          return (
            <figure
              key={s.src}
              className={`absolute inset-0 transition-all duration-700 ease-out ${
                isActive ? 'z-10 scale-100 opacity-100' : 'z-0 scale-95 opacity-0'
              }`}
            >
              <div
                className={`overflow-hidden border-[6px] ${slideAccent.border} ${frameClass} ${rotateClass} shadow-stamp-lifted`}
              >
                <img src={s.src} alt={s.alt} className="aspect-[4/5] w-full object-cover" loading={i === 0 ? 'eager' : 'lazy'} />
              </div>
              <figcaption
                className={`absolute -bottom-4 left-1/2 w-[85%] -translate-x-1/2 rounded-lg ${slideAccent.bg} px-4 py-2 text-center text-xs font-medium text-ink shadow-stamp backdrop-blur-sm sm:text-sm`}
              >
                {s.caption}
              </figcaption>
            </figure>
          );
        })}
        {/* Reserve layout height matching the active slide's aspect ratio */}
        <div className="aspect-[4/5] w-full" aria-hidden="true" />
      </div>

      {/* Slide indicator dots */}
      <div className="absolute -bottom-10 left-1/2 flex -translate-x-1/2 gap-2">
        {SLIDES.map((s, i) => (
          <button
            key={s.src}
            onClick={() => setActive(i)}
            aria-label={`Show slide ${i + 1}`}
            className={`h-2 rounded-full transition-all ${
              i === active ? `w-6 ${accent.dot}` : 'w-2 bg-ink/20'
            }`}
          />
        ))}
      </div>
    </div>
  );
}
